import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import VuiTypography from "components/VuiTypography";

// ─── Wagon SVG icon ───────────────────────────────────────────────────────────
function WagonIcon({ color = "#FFA500", textColor = "rgba(0,0,0,0.2)" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 52 36"
      width="52"
      height="36"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <rect x="2" y="4" width="48" height="18" rx="3" fill={color} opacity="0.85" />
      <rect x="5" y="8" width="8" height="10" rx="1" fill={textColor} />
      <rect x="15" y="8" width="8" height="10" rx="1" fill={textColor} />
      <rect x="25" y="8" width="8" height="10" rx="1" fill={textColor} />
      <rect x="35" y="8" width="8" height="10" rx="1" fill={textColor} />
      <rect x="0" y="11" width="3" height="4" rx="1" fill="rgba(0,0,0,0.35)" />
      <rect x="49" y="11" width="3" height="4" rx="1" fill="rgba(0,0,0,0.35)" />
      <line x1="10" y1="22" x2="10" y2="28" stroke="#888" strokeWidth="1.5" />
      <line x1="42" y1="22" x2="42" y2="28" stroke="#888" strokeWidth="1.5" />
      <circle cx="10" cy="29" r="5" fill="#555" stroke="#888" strokeWidth="1.2" />
      <circle cx="10" cy="29" r="2" fill="#888" />
      <circle cx="42" cy="29" r="5" fill="#555" stroke="#888" strokeWidth="1.2" />
      <circle cx="42" cy="29" r="2" fill="#888" />
    </svg>
  );
}

// ─── Wagon train marquee with reset animation ─────────────────────────────────
const wagonSequence = [
  { color: "#FFA500", tc: "rgba(0,0,0,0.20)" },
  { color: "#20B2AA", tc: "rgba(0,0,0,0.20)" },
  { color: "#0075FF", tc: "rgba(0,0,0,0.20)" },
  { color: "#D5ED9F", tc: "rgba(0,0,0,0.15)" },
  { color: "#FFA500", tc: "rgba(0,0,0,0.20)" },
];

// CSS keyframes for the continuous scrolling animation of the wagon train
const marqueeKeyframes = `
  @keyframes trainRoll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-100%); }
  }
`;

// Component that creates an infinite scrolling train of wagons
function ResettingWagonMarquee() {
  const doubled = [...wagonSequence, ...wagonSequence]; // Double the array for seamless looping
  return (
    <>
      <style>{marqueeKeyframes}</style>
      <div
        style={{
          overflow: "hidden", // Hides wagons outside the visible area
          borderRadius: 6,
          position: "relative",
          height: 50,
        }}
      >
        {/* Animated scrolling container */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 6,
            width: "max-content", // Width based on content for proper scrolling
            animation: "trainRoll 10s linear infinite", // Continuous infinite animation
          }}
        >
          {doubled.map((w, i) => (
            <WagonIcon key={i} color={w.color} textColor={w.tc} />
          ))}
        </div>
        {/* Track/rail lines at the bottom */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            pointerEvents: "none", // Allows clicking through to elements below
          }}
        >
          <div
            style={{
              height: 1.5,
              background: "rgba(255,255,255,0.18)",
              marginBottom: 2.5,
            }}
          />
          <div style={{ height: 1.5, background: "rgba(255,255,255,0.18)" }} />
        </div>
        {/* Fade effect on left and right edges for smooth appearance/disappearance */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, rgba(6,11,40,0.75) 0%, transparent 15%, transparent 85%, rgba(6,11,40,0.75) 100%)",
            pointerEvents: "none",
          }}
        />
      </div>
    </>
  );
}
// ─── Material Percentage Display (without donut) ─────────────────────────────
function MaterialPercentageDisplay({ percentage, barColor }) {
  return (
    <div style={{ textAlign: "center", marginBottom: 8 }}>
      <p style={{ fontSize: 32, fontWeight: 700, color: barColor, margin: 0, lineHeight: 1.2 }}>
        {percentage}%
      </p>
    </div>
  );
}

// ─── Last Train Card Component ────────────────────────────────────────────────
function LastTrainCard({
  siteId,
  gatewayName: propGatewayName,
  color = "#FFA500",
  innerCardStyle,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastTrainData, setLastTrainData] = useState(null);
  const [barColor, setBarColor] = useState(color);
  const [gatewayName, setGatewayName] = useState(propGatewayName);

  const getMaterialName = (materialCode) => {
    const materialMap = {
      1: "0/4mm (MP)",
      2: "0/4mm (Screed)",
      3: "0/20mm (Ballast)",
      4: "4/10mm (Gravel)",
      5: "4/20mm (Gravel)",
      6: "10/20mm (Gravel)",
      7: "20/40mm (Gravel)",
    };
    const code = String(materialCode || "");
    return materialMap[code] || "Unknown Material";
  };

  useEffect(() => {
    const fetchGatewayName = async () => {
      if (propGatewayName) {
        setGatewayName(propGatewayName);
        return propGatewayName;
      }

      if (!siteId) return null;

      try {
        const response = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/gateway/profile?site_id=${siteId}`
        );
        const data = await response.json();
        const name = data[0]?.name;
        setGatewayName(name);
        return name;
      } catch (error) {
        console.error("Error fetching gateway name:", error);
        return null;
      }
    };

    fetchGatewayName();
  }, [siteId, propGatewayName]);

  useEffect(() => {
    const fetchLastTrainData = async () => {
      if (!gatewayName) return;

      setLoading(true);
      setError(null);

      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        const startDateFormatted = startDate.toISOString().slice(0, 16);
        const endDateFormatted = endDate.toISOString().slice(0, 16);

        const loadsResponse = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream?query_type=train_loads&thing_name=${gatewayName}&start_date=${startDateFormatted}&end_date=${endDateFormatted}`
        );

        if (!loadsResponse.ok) {
          throw new Error(`HTTP error! status: ${loadsResponse.status}`);
        }

        const loads = await loadsResponse.json();

        const completeLoads = loads.filter(
          (load) => load.Status === 1 || load.Status === "1" || load.Status === true
        );

        if (completeLoads.length === 0) {
          setError("No complete train loads found");
          setLoading(false);
          return;
        }

        const sortedLoads = completeLoads.sort((a, b) => new Date(b.time) - new Date(a.time));
        const mostRecentLoad = sortedLoads[0];

        const reportResponse = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream?query_type=train_load_report&thing_name=${gatewayName}&train_load_id=${mostRecentLoad.Train_Load_ID}`
        );

        if (!reportResponse.ok) {
          throw new Error(`HTTP error! status: ${reportResponse.status}`);
        }

        const reportData = await reportResponse.json();

        if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
          setError("No report data found");
          setLoading(false);
          return;
        }

        const latestRecord = reportData[reportData.length - 1];

        const noWagons = parseInt(latestRecord.No_Wagons) || 0;
        let totalTonnage = 0;

        for (let i = 1; i <= noWagons; i++) {
          const loadedTonnage = parseFloat(latestRecord[`Wagon_${i}_Loaded_Tonnage`]) || 0;
          totalTonnage += loadedTonnage;
        }

        const targetTonnage = parseFloat(latestRecord.Target_Tonnage) || totalTonnage;
        const achievedPercentage = targetTonnage > 0 ? (totalTonnage / targetTonnage) * 100 : 100;

        const barColorValue =
          achievedPercentage >= 99 ? "#4CAF50" : achievedPercentage >= 95 ? "#FFC107" : "#F44336";
        setBarColor(barColorValue);

        const timeValue = latestRecord.time || latestRecord.timestamp;
        let formattedDateTime = "Unknown";
        if (timeValue) {
          try {
            const date = new Date(timeValue);
            // ✅ Let the browser handle GMT/BST automatically
            const formatted = date.toLocaleString("en-GB", {
              timeZone: "Europe/London",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
            // toLocaleString returns "DD/MM/YYYY, HH:MM" — strip the comma
            formattedDateTime = formatted.replace(",", "");
          } catch (error) {
            console.error("Error parsing date:", error);
          }
        }

        setLastTrainData({
          dateTime: formattedDateTime,
          destination: latestRecord.Destination || "Unknown",
          brettRefNumber: latestRecord.Brett_Reference_No || "N/A",
          noWagons: noWagons.toString(),
          totalWagons: noWagons.toString(),
          trainLoadId: mostRecentLoad.Train_Load_ID,
          totalTonnage: totalTonnage.toFixed(2),
          targetTonnage: targetTonnage.toFixed(2),
          achievedPercentage: achievedPercentage.toFixed(2),
        });
      } catch (error) {
        console.error("Error fetching last train data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (gatewayName) {
      fetchLastTrainData();
    }
  }, [gatewayName]);

  if (loading) {
    return (
      <div style={innerCardStyle}>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <VuiTypography variant="caption" color="textSecondary">
            Loading last train data...
          </VuiTypography>
        </div>
      </div>
    );
  }

  if (error || !lastTrainData) {
    return (
      <div style={innerCardStyle}>
        <div style={{ textAlign: "center", padding: "20px" }}>
          <VuiTypography variant="caption" color="error">
            {error || "No last train data available"}
          </VuiTypography>
        </div>
      </div>
    );
  }

  return (
    <div style={innerCardStyle}>
      <p
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.6)",
          margin: "0 0 12px",
          textAlign: "center",
        }}
      >
        Last Train
      </p>
      {/* Percentage value without donut */}
      <MaterialPercentageDisplay
        percentage={lastTrainData.achievedPercentage}
        barColor={barColor}
      />

      {/* Tonnage achieved bar at the top */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 4,
          }}
        >
          {/* <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Tonnage achieved</span> */}
          {/* <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
            {lastTrainData.achievedPercentage}%
          </span> */}
        </div>
        <div
          style={{ background: `${barColor}26`, borderRadius: 4, height: 5, overflow: "hidden" }}
        >
          <div
            style={{
              background: barColor,
              height: "100%",
              width: `${Math.min(parseFloat(lastTrainData.achievedPercentage), 100)}%`,
              borderRadius: 4,
              transition: "width 0.3s ease",
            }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>
            {lastTrainData.targetTonnage} T target
          </span>
          <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)" }}>
            {lastTrainData.totalTonnage} T actual
          </span>
        </div>
      </div>

      {/* Train animation */}
      <div style={{ marginTop: 8, marginBottom: 12 }}>
        <ResettingWagonMarquee totalWagons={parseInt(lastTrainData.totalWagons) || 0} />
      </div>

      {/* Date Time, Destination, Brett Ref Number, Number of Wagons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Date / Time</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>
            {lastTrainData.dateTime}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Destination</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>
            {lastTrainData.destination}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Brett Ref Number</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>
            {lastTrainData.brettRefNumber}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Number of Wagons</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.75)" }}>
            {lastTrainData.totalWagons}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Mini Doughnut Component ──────────────────────────────────────────────────
function MiniDoughnut({ value, color, label, size = 70 }) {
  const remaining = 100 - value;
  const data = [
    { name: "loss", value: value },
    { name: "remaining", value: remaining },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <PieChart width={size} height={size}>
          <Pie
            data={data}
            dataKey="value"
            cx={size / 2}
            cy={size / 2}
            innerRadius={size * 0.32}
            outerRadius={size * 0.44}
            startAngle={90}
            endAngle={-270}
            strokeWidth={0}
            isAnimationActive
          >
            <Cell fill={color} />
            <Cell fill="rgba(255,255,255,0.08)" />
          </Pie>
        </PieChart>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <p style={{ fontSize: 16, fontWeight: 700, color: color, margin: 0, lineHeight: 1 }}>
            {value}%
          </p>
        </div>
      </div>
      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", margin: 0 }}>{label}</p>
    </div>
  );
}

// ─── Total Tonnage Card (UNCHANGED) ───────────────────────────────────────────
function TotalTonnageCard({ tonnageData, unit = "T", color = "#20B2AA" }) {
  const periods = [
    { key: "day", label: "Day", value: tonnageData.day },
    { key: "week", label: "Week", value: tonnageData.week },
    { key: "month", label: "Month", value: tonnageData.month },
    { key: "year", label: "Year", value: tonnageData.year },
  ];
  return (
    <div style={innerCardStyle}>
      <p
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.6)",
          margin: "0 0 12px",
          textAlign: "center",
        }}
      >
        Total Tonnage
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {periods.map((period) => {
          const progress = period.value > 0 ? 100 : 0;
          return (
            <div key={period.key} style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{period.label}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color }}>
                  {period.value.toLocaleString()}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: "rgba(255,255,255,0.4)",
                      marginLeft: 2,
                    }}
                  >
                    {unit}
                  </span>
                </span>
              </div>
              <div
                style={{
                  backgroundColor: `${color}33`,
                  borderRadius: 4,
                  overflow: "hidden",
                  height: 6,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    backgroundColor: color,
                    height: "100%",
                    width: `${progress}%`,
                    borderRadius: 4,
                    transition: "width 0.3s ease",
                    opacity: period.value > 0 ? 1 : 0.3,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Loss Revenue Card (UNCHANGED) ────────────────────────────────────────────
function LossRevenueCard({ lossData }) {
  return (
    <div style={innerCardStyle}>
      <div
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.6)",
          margin: "0 0 8px",
          textAlign: "center",
        }}
      >
        Loss Revenue
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <LossDonut value={lossData.day} label="Day" size={100} />
        <div style={{ display: "flex", gap: 55, justifyContent: "center" }}>
          <LossDonut value={lossData.week} label="Week" size={70} />
          <LossDonut value={lossData.month} label="Month" size={70} />
          <LossDonut value={lossData.year} label="Year" size={70} />
        </div>
      </div>
    </div>
  );
}

// ─── Loss Donut Component (UNCHANGED) ─────────────────────────────────────────
function LossDonut({ value, label, size = 100 }) {
  const getColor = (v) => (v < 2 ? "#4CAF50" : v < 4 ? "#FFC107" : "#F44336");
  const color = getColor(value);
  const data = [
    { name: "loss", value: value },
    { name: "remaining", value: Math.max(0, 10 - value) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <PieChart width={size} height={size}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={size * 0.32}
            outerRadius={size * 0.45}
            startAngle={90}
            endAngle={-270}
            dataKey="value"
            stroke="none"
          >
            <Cell fill={color} />
            <Cell fill="rgba(255,255,255,0.08)" />
          </Pie>
        </PieChart>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: size * 0.22, fontWeight: 700, color }}>{value}%</span>
        </div>
      </div>
      <span
        style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.5)",
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// ─── Tonnage Per Material Line Chart (UNCHANGED) ──────────────────────────────
const calculatePercentages = (rawData) =>
  rawData.map((week) => {
    const total = week.test1 + week.test2 + week.test3 + week.test4;
    return {
      label: week.label,
      test1: total > 0 ? (week.test1 / total) * 100 : 0,
      test2: total > 0 ? (week.test2 / total) * 100 : 0,
      test3: total > 0 ? (week.test3 / total) * 100 : 0,
      test4: total > 0 ? (week.test4 / total) * 100 : 0,
    };
  });

function PercentageTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "rgba(6,11,40,0.97)",
        border: "0.5px solid rgba(255,255,255,0.15)",
        borderRadius: 8,
        padding: "4px 8px",
        fontSize: 10,
      }}
    >
      <p style={{ margin: "0 0 2px", color: "rgba(255,255,255,0.5)" }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: "2px 0", color: entry.color }}>
          {entry.dataKey}: {Number(entry.value).toFixed(1)}%
        </p>
      ))}
    </div>
  );
}

function TonnagePerMaterialChart({ percentageData, materialBreakdown }) {
  const BREAKDOWN_COLORS = ["#FFA500", "#20B2AA", "#0075FF", "#D5ED9F"];

  return (
    <div style={innerCardStyle}>
      <p
        style={{
          fontSize: 13,
          color: "rgba(255,255,255,0.6)",
          margin: "0 0 8px",
          textAlign: "center",
        }}
      >
        Tonnage Per Material (%)
      </p>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px 10px",
          marginBottom: 8,
          justifyContent: "center",
        }}
      >
        {materialBreakdown.map((m, i) => (
          <span
            key={m.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 9,
              color: "rgba(255,255,255,0.55)",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 2,
                background: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length],
              }}
            />
            {m.name}
          </span>
        ))}
      </div>
      <div style={{ position: "relative", width: "100%", height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={percentageData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 8, fill: "rgba(255,255,255,0.4)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 8, fill: "rgba(255,255,255,0.4)" }}
              axisLine={false}
              tickLine={false}
              tickCount={5}
              label={{
                value: "%",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 8, fill: "rgba(255,255,255,0.4)" },
              }}
            />
            <Tooltip content={<PercentageTooltip />} />
            {materialBreakdown.map((m, i) => (
              <Line
                key={m.name}
                type="monotone"
                dataKey={m.name}
                stroke={BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 2, fill: BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length] }}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const mainCardStyle = {
  background: "linear-gradient(127deg, rgba(6,11,40,0.94) 19%, rgba(10,14,35,0.7) 77%)",
  border: "0.5px solid rgba(255,255,255,0.12)",
  borderRadius: 16,
  padding: 16,
  width: "100%",
  boxSizing: "border-box",
};

const innerCardStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "0.5px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: "12px",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
};

const rowStyle = {
  display: "flex",
  flexDirection: "row",
  gap: "16px",
  alignItems: "stretch",
  flexWrap: "wrap",
};

// ─── Simulated data for other cards ──────────────────────────────────────────
const SIMULATED = {
  totalWagons: 18,
  totalTons: 18240,
  ratioHistoryRaw: [
    { label: "W1", test1: 84, test2: 31, test3: 20, test4: 18 },
    { label: "W2", test1: 47, test2: 33, test3: 22, test4: 19 },
    { label: "W3", test1: 43, test2: 30, test3: 64, test4: 17 },
    { label: "W4", test1: 51, test2: 35, test3: 24, test4: 21 },
    { label: "W5", test1: 48, test2: 32, test3: 21, test4: 20 },
    { label: "W6", test1: 55, test2: 37, test3: 25, test4: 22 },
    { label: "W7", test1: 50, test2: 50, test3: 23, test4: 21 },
    { label: "W8", test1: 53, test2: 36, test3: 24, test4: 22 },
  ],
  materialBreakdown: [
    { name: "test1", value: 7420 },
    { name: "test2", value: 4810 },
    { name: "test3", value: 3200 },
    { name: "test4", value: 2810 },
  ],
  lossRevenueData: { day: 2.4, week: 3.1, month: 2.8, year: 2.2 },
  tonnageData: { day: 620, week: 4280, month: 18240, year: 218880 },
};

// ─── Main Component (UNCHANGED except for LastTrainCard integration) ──────────
export default function ProductionCardTrainLoad({
  title = "Train Loading",
  siteId = null,
  gatewayName = null,
  totalWagons = SIMULATED.totalWagons,
  tonnageData = SIMULATED.tonnageData,
  lossRevenueData = SIMULATED.lossRevenueData,
  materialBreakdown = SIMULATED.materialBreakdown,
  ratioHistoryRaw = SIMULATED.ratioHistoryRaw,
  wagonColor = "#FFA500",
  tonnageColor = "#20B2AA",
}) {
  const percentageData = calculatePercentages(ratioHistoryRaw);

  return (
    <div style={mainCardStyle}>
      <div style={{ width: "100%", textAlign: "center" }}>
        <VuiTypography
          variant="sm"
          color="white"
          fontWeight="bold"
          sx={{
            mb: 2,
            fontSize: {
              xs: "0.8rem",
              sm: "0.9rem",
              md: "1rem",
              lg: "1.2rem",
            },
            display: "inline-block",
          }}
        >
          {title}
        </VuiTypography>
      </div>
      <div style={rowStyle}>
        {/* Card 1: Last Train - Fetches data from backend */}
        <div style={{ flex: 1, minWidth: "150px" }}>
          <LastTrainCard
            siteId={siteId}
            gatewayName={gatewayName}
            color={wagonColor}
            innerCardStyle={innerCardStyle}
          />
        </div>

        {/* Card 2: Total Tonnage with time periods (UNCHANGED) */}
        <div style={{ flex: 1, minWidth: "150px" }}>
          <TotalTonnageCard tonnageData={tonnageData} unit="T" color={tonnageColor} />
        </div>

        {/* Card 3: Loss Revenue donut charts (UNCHANGED) */}
        <div style={{ flex: 1, minWidth: "150px" }}>
          <LossRevenueCard lossData={lossRevenueData} />
        </div>

        {/* Card 4: Material percentage line chart (UNCHANGED) */}
        <div style={{ flex: 1, minWidth: "200px" }}>
          <TonnagePerMaterialChart
            percentageData={percentageData}
            materialBreakdown={materialBreakdown}
          />
        </div>
      </div>
    </div>
  );
}
